import {
  CustomApiError,
  type CustomResult,
  useGetMembersPlaces,
  useGetMembersPlacesByPlaceId,
  useGetOnboardingsByOnboardingId,
  useSessionToken,
} from "@birdwatch/client";
import { useQueryClient } from "@tanstack/react-query";
import { useMatchRoute } from "@tanstack/react-router";
import { isEmpty, isUndefined } from "lodash-es";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { APP_ROUTERS } from "@/constants/app-routers";
import { useMyPlaceIdStorage } from "@/features/my/_hooks/use-my-place-id-storage";
import { useMyPlaces } from "@/features/my/_hooks/use-my-places";
import { findDefaultPlace } from "@/features/my/_libs/find-default-place";
import {
  createPlaceSwitch,
  type SwitchPlace,
} from "@/features/my/_libs/place-switch";
import type { MyPlaceListItem, PlaceDetail } from "@/features/my/_types";
import { redirectOnboardingId } from "@/features/my/onboarding/onboarding-redirect";
import { useImpersonationSession } from "@/features/shared/impersonation";

export { clearMyPlaceIdStorage } from "@/features/my/_hooks/use-my-place-id-storage";

type MyPlaceContextType = {
  placeId: string | undefined;
  setPlaceId: (placeId: string | undefined) => void;
  /**
   * Move to another of the member's homes without the full-screen loading overlay:
   * the home's detail is fetched first, so the id changes only once it is cached.
   * `onCommit` settles before the id changes (a URL rewrite the new home's page must not
   * see undone). A newer switch supersedes one still in flight.
   */
  switchPlace: SwitchPlace;
  /** The home a switcher names: the one being switched to, from the click on. */
  selectedPlaceId: string | undefined;
  /**
   * The selected home's detail. While `isChangingPlace`, it is still the home last shown,
   * so the shell around the waiting page never sees an empty place.
   */
  place: PlaceDetail | null;
  places: MyPlaceListItem[];
  isLoading: boolean;
  /** The current step of the home's v7 onboarding, which the onboarding redirects read. */
  onboardingStep: string | undefined;
  /** The step above is still loading; it is part of the home being ready to show. */
  isLoadingOnboardingStep: boolean;
  /**
   * The home or its onboarding step is loading after a home has already been shown: the
   * shell stays up and only the page waits, instead of the whole member tree giving way to
   * the loading overlay.
   */
  isChangingPlace: boolean;
};

function isMembershipGone(error: unknown): boolean {
  return (
    error instanceof CustomApiError &&
    (error.status === 403 || error.status === 404)
  );
}

// Under the ~10s request budget; past it the switch commits and the detail query takes over.
const SWITCH_LOAD_DEADLINE_MS = 8000;

export const MyPlaceContext = createContext<MyPlaceContextType | undefined>(
  undefined,
);

export function useMyPlaceContext(): MyPlaceContextType {
  const context = useContext(MyPlaceContext);
  if (!context) {
    throw new Error(
      "useMyPlaceContext must be used within MyPlaceContextProvider",
    );
  }
  return context;
}

type MyPlaceContextProviderProps = {
  children: ReactNode;
};

export function MyPlaceContextProvider({
  children,
}: MyPlaceContextProviderProps) {
  const queryClient = useQueryClient();
  const matchRoute = useMatchRoute();
  const [placeId, setPlaceId] = useMyPlaceIdStorage();
  /** Avoid spamming invalidation while list is stale but member detail already validates placeId. */
  const listRefreshIssuedRef = useRef(false);
  const impersonation = useImpersonationSession();
  /** The admin's pick is applied once per frame; after that the member's own switcher rules. */
  const impersonationPlaceSeededRef = useRef(false);
  // Prefix of every member places list query, whatever its `take` — what a refresh targets.
  const memberPlacesQueryKey = useGetMembersPlaces().getQueryKey();

  // Address lookup steals window focus (Google Places autocomplete); skip refetch on that route.
  const refetchOnWindowFocus = !matchRoute({ to: APP_ROUTERS.MY_PLACES_ADD });

  // Fetch list of places for the switcher
  const { places, isLoading: isLoadingPlaces } = useMyPlaces({
    refetchOnWindowFocus,
  });

  // Fetch detailed place data for the selected place (managers, members, latestMembership, etc.).
  const placeQuery = useGetMembersPlacesByPlaceId({
    placeId: placeId || "",
  }).get({ enabled: !!placeId, refetchOnWindowFocus });
  // 403/404 = the caller is no longer a member of this home (an owner removed
  // them, or the stored id is stale). A failed refetch keeps the last good
  // `data`, so read the status: otherwise a removed member stays on the home.
  const isPlaceGone =
    placeQuery.error?.status === 403 || placeQuery.error?.status === 404;
  const loadedPlace = isPlaceGone
    ? null
    : ((placeQuery.data ?? null) as PlaceDetail | null);
  const isLoadingPlace = placeQuery.isLoading;

  const onboardingId = redirectOnboardingId(loadedPlace) ?? "";
  const onboardingStepQuery = useGetOnboardingsByOnboardingId({
    onboardingId,
  }).get({ enabled: !!onboardingId });
  const isLoadingOnboardingStep =
    !!onboardingId && onboardingStepQuery.isLoading;

  const getToken = useSessionToken();
  const [switchingPlaceId, setSwitchingPlaceId] = useState<string>();
  const switchPlace = useMemo(() => {
    const placeBuilder = (id: string) =>
      useGetMembersPlacesByPlaceId({ placeId: id });
    const stepBuilder = (id: string) =>
      useGetOnboardingsByOnboardingId({ onboardingId: id });
    const isCachedKey = (queryKey: readonly unknown[]) =>
      queryClient.getQueryData(queryKey) !== undefined;

    // One try per read. A read that fails is reset to empty, so the mounted query loads it
    // again with its usual retries (the page waits in the shell meanwhile) — except a home
    // the member lost (403/404), whose error is kept so the fallback below fires at once.
    const fetchOnce = async <T,>(
      builder: {
        getQueryKey: () => readonly unknown[];
        fetch: (token: string | null) => Promise<CustomResult<T>>;
      },
      keepsError: (error: unknown) => boolean,
    ): Promise<T> => {
      const queryKey = builder.getQueryKey();
      const token = await getToken();
      try {
        const result = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => builder.fetch(token),
          retry: false,
        });
        return result.data;
      } catch (error) {
        if (!keepsError(error)) {
          await queryClient.resetQueries({ queryKey, exact: true });
        }
        throw error;
      }
    };

    // Everything the page needs before it can show: the home, then its onboarding step.
    const loadHome = async (id: string) => {
      const detail = await fetchOnce(placeBuilder(id), isMembershipGone);
      const nextOnboardingId = redirectOnboardingId(detail as PlaceDetail);
      if (nextOnboardingId) {
        await fetchOnce(stepBuilder(nextOnboardingId), () => false);
      }
    };

    return createPlaceSwitch({
      isCached: (id) => {
        const cached = queryClient.getQueryData<CustomResult<PlaceDetail>>(
          placeBuilder(id).getQueryKey(),
        );
        if (cached === undefined) return false;
        const nextOnboardingId = redirectOnboardingId(cached.data);
        return (
          !nextOnboardingId ||
          isCachedKey(stepBuilder(nextOnboardingId).getQueryKey())
        );
      },
      // Bounded: past the deadline the switch commits, and the read still in flight
      // lands in the query the page then waits on.
      load: async (id) => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const deadline = new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error("Place switch timed out")),
            SWITCH_LOAD_DEADLINE_MS,
          );
        });
        try {
          await Promise.race([loadHome(id), deadline]);
        } finally {
          clearTimeout(timer);
        }
      },
      setPending: setSwitchingPlaceId,
      commit: setPlaceId,
    });
  }, [getToken, queryClient, setPlaceId]);

  // Combined loading state
  const isLoading = useMemo(() => {
    if (isLoadingPlaces) {
      return true;
    }
    if (isLoadingPlace) {
      return true;
    }
    if (!isEmpty(places) && isUndefined(placeId)) {
      return true;
    }
    return false;
  }, [isLoadingPlaces, placeId, isLoadingPlace, places]);

  // The home last shown in full. Once there is one, a later load is a change of home, not
  // the app starting, and the shell keeps showing this home until the next one is ready.
  const [shownPlace, setShownPlace] = useState<PlaceDetail | null>(null);
  const isReady = !isLoading && !isLoadingOnboardingStep;
  useEffect(() => {
    if (isReady && loadedPlace) setShownPlace(loadedPlace);
  }, [isReady, loadedPlace]);
  const isChangingPlace = !isReady && shownPlace !== null;
  const place = isChangingPlace ? shownPlace : loadedPlace;

  // Initialize placeId to an active place if not set yet or if stored placeId is invalid
  useEffect(() => {
    // Removed from their only home: forget it, as there is no default to pick.
    if (!isLoadingPlaces && places.length === 0 && placeId && isPlaceGone) {
      setPlaceId(undefined);
      return;
    }
    if (!isLoadingPlaces && places.length > 0) {
      const defaultPlace = findDefaultPlace(places);
      const inList = placeId ? places.some((p) => p.id === placeId) : false;

      // In the admin impersonation frame, open on the place the admin picked,
      // once, overriding whatever the stored placeId holds (storage is shared
      // with the admin's own tab). Handled here, ahead of the default-place
      // fallback below, so an empty store cannot be filled with the default in
      // the same pass and win the race.
      const pickedPlaceId = impersonation?.target.placeId;
      if (
        pickedPlaceId &&
        !impersonationPlaceSeededRef.current &&
        places.some((p) => p.id === pickedPlaceId)
      ) {
        impersonationPlaceSeededRef.current = true;
        if (placeId !== pickedPlaceId) {
          setPlaceId(pickedPlaceId);
          return;
        }
      }

      if (placeId && inList) {
        listRefreshIssuedRef.current = false;
      }

      if (placeId && !inList) {
        // After checkout, `/members/places` often lags behind the new membership — member detail is authoritative.
        if (isLoadingPlace) {
          return;
        }
        if (loadedPlace?.id === placeId) {
          if (!listRefreshIssuedRef.current) {
            listRefreshIssuedRef.current = true;
            void queryClient.invalidateQueries({
              queryKey: memberPlacesQueryKey,
            });
          }
          return;
        }
        listRefreshIssuedRef.current = false;
        setPlaceId(defaultPlace?.id);
      } else if (!placeId) {
        listRefreshIssuedRef.current = false;
        setPlaceId(defaultPlace?.id);
      }
    }
  }, [
    impersonation,
    isLoadingPlaces,
    isLoadingPlace,
    isPlaceGone,
    loadedPlace,
    places,
    placeId,
    queryClient,
    setPlaceId,
  ]);

  return (
    <MyPlaceContext.Provider
      value={{
        placeId,
        setPlaceId,
        switchPlace,
        selectedPlaceId: switchingPlaceId ?? placeId,
        place,
        places,
        isLoading,
        onboardingStep: onboardingStepQuery.data?.currentStep ?? undefined,
        isLoadingOnboardingStep,
        isChangingPlace,
      }}
    >
      {children}
    </MyPlaceContext.Provider>
  );
}
